/**
 * Plan 109 Release B — client-side preparation for filter-label photo
 * imports. Downscales to the Bedrock high-resolution ceiling (2576 px on the
 * long edge) and re-encodes as JPEG before upload: shrinks the S3 object and
 * matches what the extraction worker would otherwise do server-side.
 */

export const IMPORT_MAX_PHOTOS = 3;
export const IMPORT_MAX_BYTES_PER_PHOTO = 3_750_000;
export const IMPORT_MAX_EDGE_PX = 2576;

/** Downscale + re-encode a picked file as JPEG (quality 0.85). */
export async function prepareImportImage(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error(`Could not read ${file.name} as an image`));
      el.src = url;
    });
    const scale = Math.min(
      1, IMPORT_MAX_EDGE_PX / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unavailable');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85));
    if (!blob) throw new Error(`Re-encoding ${file.name} failed`);
    if (blob.size > IMPORT_MAX_BYTES_PER_PHOTO) {
      throw new Error(`${file.name} is still over the 3.75 MB limit after resizing`);
    }
    return blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Lowercase hex SHA-256 of a blob (matches the API's digest format). */
export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}
